/**
 * CategoriesZone — TELV1-296 UX commit 6 + TELV1-332 (concept-cloud reframe).
 *
 * Two stacked blocks below the per-question summary:
 *
 *   1. Categories — the existing thematic category breakdown (left
 *      untouched here; rendered by the shared <Categories> component).
 *
 *   2. Distinctive concepts — a sentiment-coloured concept cloud built
 *      from the analysis Tellet already produced (themes + voice quotes
 *      with one small LLM noun-phrase extraction pass). Sized by the
 *      number of UNIQUE RESPONDENTS who expressed each concept — not by
 *      raw mention count — which sidesteps the filler-word problem of a
 *      naive frequency cloud (where words like "yeah" outsize words
 *      like "maneuverable"). When the analysis is too sparse for a
 *      cloud (< 5 concepts) the block falls back to a ranked horizontal
 *      bar list using the same sentiment palette. The PNG download (⋯
 *      menu) wraps the white capture card so what's exported matches
 *      what's on screen.
 */
import { useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SearchParam } from '@common/ts/enums';
import { Loader2, MoreHorizontal, Download, Check } from 'lucide-react';
import useTranslation from '@/hooks/useTranslation';
import Categories from '../Categories';
import type { CategoryApiResponse, SelectedCategory } from '@/types';
import { useGetQuestionConcepts, type ConceptEntry, type ConceptSentiment } from '@/reactQuery/get';
import WordCloud, { type WordCloudItem } from '@/components/WordCloud/WordCloud';
import useDownloadImage from '@/pages/projects/single-project/Content/research_assistant/useDownloadImage';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

interface CategoriesZoneProps {
  categories?: CategoryApiResponse;
  isPendingCategories: boolean;
  analysisLanguage: string;
  onSelectCategory: (category: SelectedCategory) => void;
  projectId?: string;
  questionID: string;
  questionText: string;
  isPendingInterviewQuestions: boolean;
  showCategoryLoadingIndicator: boolean;
}

// Minimum concepts for the cloud layout to read well. Below this we render
// a ranked horizontal bar list (Displayr's evidence: bars beat clouds at
// low counts on every dimension that matters for decision-making).
const SPARSE_CLOUD_THRESHOLD = 5;

// Semantic palette mirrors the WordCloud component's SENTIMENT_COLORS.
const SENTIMENT_BAR_COLORS: Record<ConceptSentiment, string> = {
  positive: '#096846',
  negative: '#B83060',
  mixed: '#7A5060',
  neutral: '#2A3547',
};

/** Map a backend Concept onto the WordCloud's item shape. */
function conceptToWordCloudItem(c: ConceptEntry): WordCloudItem {
  return {
    text: c.phrase,
    value: Math.max(1, c.unique_respondents),
    sentiment: c.sentiment,
    tooltip: `${c.phrase} — ${c.unique_respondents} respondent${
      c.unique_respondents === 1 ? '' : 's'
    } (${c.sentiment})`,
    payload: c,
  };
}

/**
 * Sparse-data fallback. Renders concepts as a ranked horizontal bar list
 * using the same sentiment palette. No external chart library needed.
 */
function ConceptBarList({ concepts, maxValue }: { concepts: ConceptEntry[]; maxValue: number }) {
  return (
    <ul className="flex flex-col gap-2">
      {concepts.map((c) => {
        const pct = Math.max(2, Math.round((c.unique_respondents / maxValue) * 100));
        const color = SENTIMENT_BAR_COLORS[c.sentiment];
        return (
          <li key={c.phrase} className="flex items-center gap-3">
            <span
              className="text-[13px] text-neutral-700 truncate min-w-[6rem] max-w-[40%]"
              title={`${c.phrase} (${c.sentiment})`}
            >
              {c.phrase}
            </span>
            <span className="flex-1 h-3 rounded-sm bg-neutral-100 overflow-hidden">
              <span
                className="block h-full rounded-sm"
                style={{ width: `${pct}%`, backgroundColor: color }}
              />
            </span>
            <span className="text-[12px] tabular-nums text-neutral-500 min-w-[2rem] text-right">
              {c.unique_respondents}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

function CategoriesZone({
  categories,
  isPendingCategories,
  analysisLanguage,
  onSelectCategory,
  projectId,
  questionID,
  questionText,
  isPendingInterviewQuestions,
  showCategoryLoadingIndicator,
}: CategoriesZoneProps) {
  const [searchParams] = useSearchParams();
  const filterId = searchParams.get(SearchParam.FILTER_ID);
  const { lang } = useTranslation();

  const { data: conceptsData, isLoading: isConceptsLoading } = useGetQuestionConcepts(
    projectId,
    questionID,
    filterId,
  );

  const concepts = useMemo(() => conceptsData?.concepts ?? [], [conceptsData?.concepts]);
  const totalRespondents = conceptsData?.scope?.total_unique_respondents ?? 0;
  const hasConcepts = concepts.length > 0;
  const isSparse = hasConcepts && concepts.length < SPARSE_CLOUD_THRESHOLD;

  const cloudItems = useMemo(() => concepts.map(conceptToWordCloudItem), [concepts]);

  // PNG export — wraps the white capture card so the exported image matches
  // what the user sees (TELV1-332, reusing 331's useDownloadImage hook).
  const cloudCardRef = useRef<HTMLDivElement>(null);
  const [isDownloadingCloud, isDoneCloud, triggerCloudDownload] = useDownloadImage(cloudCardRef, {
    title: questionText,
    errorMessage: lang.get('msg.downloadFailed'),
  });

  return (
    <div className="flex flex-col gap-5 rounded-xl bg-neutral-50/50 p-5 -mx-1">
      <Categories
        projectId={projectId}
        questionID={questionID}
        questionText={questionText}
        analysisLanguage={analysisLanguage}
        onSelectCategory={onSelectCategory}
        categories={categories}
        isPendingCategories={isPendingCategories}
        isPendingInterviewQuestions={isPendingInterviewQuestions}
        showCategoryLoadingIndicator={showCategoryLoadingIndicator}
      />

      {/* Distinctive concepts — TELV1-332 */}
      <hr className="border-neutral-200" />

      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-neutral-700">
            {lang.get('msg.distinctiveConcepts')}
          </h3>

          {/* More actions — Download PNG (only when there's content) */}
          {hasConcepts && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  type="button"
                  aria-label={lang.get('msg.downloadWordCloudPng')}
                  className="focus-visible:ring-2 focus-visible:ring-primary/30"
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={triggerCloudDownload}
                  disabled={isDownloadingCloud}
                  className="flex items-center gap-2"
                >
                  {/* eslint-disable-next-line no-nested-ternary */}
                  {isDownloadingCloud ? (
                    <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />
                  ) : isDoneCloud ? (
                    <Check className="h-4 w-4 text-primary" />
                  ) : (
                    <Download className="h-4 w-4 text-neutral-400" />
                  )}
                  {lang.get('msg.downloadWordCloudPng')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {isConceptsLoading && !hasConcepts ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-neutral-400" />
          </div>
        ) : (
          <div ref={cloudCardRef} className="bg-white border border-neutral-200 rounded-lg p-4">
            {isSparse ? (
              <ConceptBarList
                concepts={concepts}
                maxValue={Math.max(1, ...concepts.map((c) => c.unique_respondents))}
              />
            ) : (
              <WordCloud words={cloudItems} height={220} />
            )}
            {hasConcepts && totalRespondents > 0 && (
              <p className="text-[11px] text-neutral-400 text-center mt-3">
                Based on {totalRespondents} respondent
                {totalRespondents === 1 ? '' : 's'}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default CategoriesZone;
